import { getUniqueId } from "@gettactic/helpers";
import Konva from "konva";
import { Options, useHotkeys } from "react-hotkeys-hook";
import { WallDoor, WallPathShape, WallShape } from "../../../../../types";
import {
  getCursorPositionOnWall,
  getCursorPositionOnWallPath
} from "../../../../../utils/wallCursorPosition";
import { useMapCanvasShapes } from "../../../contexts/MapCanvasContext";
import { useMapCanvasStage } from "../../../contexts/MapCanvasContext/useMapCanvasStage";
import {
  useMapEditorActions,
  useMapEditorSelection
} from "../../../contexts/MapEditorContext";
import { DEFAULT_DOOR_WIDTH } from "../../../defines";
import { useEditorShortcuts } from "../EditorShortcutContext/useEditorShortcuts";
import { GLOBAL_SHORTCUTS_SCOPE } from "../shortcuts";

export const useAddDoorShortcut = (
  stageRef: React.RefObject<Konva.Stage | null>
) => {
  const { selectedIds } = useMapEditorSelection();
  const { updateShape } = useMapEditorActions();
  const { shapes } = useMapCanvasShapes();
  const { stagePositionRef } = useMapCanvasStage();
  const { shortcuts } = useEditorShortcuts();

  const selectedShape =
    selectedIds.length === 1
      ? shapes.find(
          (s) =>
            s.id === selectedIds[0] &&
            (s.type === "wall" || s.type === "wall-path")
        )
      : undefined;

  const shortcutOptions: Options = {
    scopes: [GLOBAL_SHORTCUTS_SCOPE],
    enabled: !!selectedShape,
    preventDefault: true
  };

  useHotkeys(
    shortcuts.global.addDoor,
    () => {
      if (!selectedShape) return;

      const stage = stageRef.current;
      if (!stage) return;

      const pointerPos = stage.getPointerPosition();
      if (!pointerPos) return;

      const stagePosition = stagePositionRef.current;
      const cursor = {
        x: (pointerPos.x - stagePosition.x) / stagePosition.scale,
        y: (pointerPos.y - stagePosition.y) / stagePosition.scale
      };

      if (selectedShape.type === "wall") {
        const wall = selectedShape as WallShape;
        const position = getCursorPositionOnWall(cursor, wall);
        const newDoor: WallDoor = {
          id: `door-${getUniqueId()}`,
          position,
          width: DEFAULT_DOOR_WIDTH
        };
        const doors = [...(wall.doors || []), newDoor];
        updateShape(wall.id, { doors } as Partial<WallShape>);
      } else if (selectedShape.type === "wall-path") {
        const path = selectedShape as WallPathShape;
        const result = getCursorPositionOnWallPath(cursor, path);
        if (!result) return;
        const newDoor: WallDoor = {
          id: `door-${getUniqueId()}`,
          position: result.position,
          width: DEFAULT_DOOR_WIDTH
        };
        const newEdges = [...path.edges];
        newEdges[result.edgeIndex] = {
          ...newEdges[result.edgeIndex],
          doors: [...(newEdges[result.edgeIndex].doors || []), newDoor]
        };
        updateShape(path.id, { edges: newEdges } as Partial<WallPathShape>);
      }
    },
    shortcutOptions,
    [shortcuts.global.addDoor, selectedShape]
  );
};
